import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Switch } from 'src/core/interim/Switch';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useSyncCenterSettingsWithMutation } from 'src/hooks/api/useSyncCenterSettingsWithMutation';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import { SyncCenterFieldValues } from '../SyncCenter/SyncCenter.types';
import {
  setValueAsNumber,
  toSyncCenterFieldValues,
  toSyncCenterSettings,
} from '../SyncCenter/SyncCenter.utils';
import * as styles from './InventorySetting.css';

export function InventorySetting() {
  const hasAutoSplitDiscontinuousListingSettingFeature = useUserHasFeature(
    Feature.AutoSplitDiscontinuousListingSetting
  );
  const hasDefaultManualMaxDisplayQuantitySettingFeature = useUserHasFeature(
    Feature.DefaultManualMaxDisplayQuantitySetting
  );

  const greaterThanZeroRequired = useContent(ContentId.GreaterThanZeroRequired);

  const { query, mutation } = useSyncCenterSettingsWithMutation();

  const formDefaultValues = useMemo(() => {
    if (query.data) {
      return toSyncCenterFieldValues(cloneDeep(query.data));
    }
    return undefined;
  }, [query.data]);

  const { control, register, handleSubmit, reset, formState } =
    useForm<SyncCenterFieldValues>({
      defaultValues: formDefaultValues,
    });

  useEffect(() => {
    reset(formDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  if (query.isLoading) {
    return <PosSpinner />;
  }

  return (
    <div
      className={styles.sectionContainer}
      style={{ gap: vars.spacing['xl'] }}
    >
      <h5 className={styles.sectionHeading}>
        <Content id={ContentId.Inventory} />
      </h5>
      <Stack direction="column" gap="xl">
        <div className={styles.label}>
          <Content id={ContentId.Listings} />
        </div>
        {hasDefaultManualMaxDisplayQuantitySettingFeature && (
          <Stack direction="column" gap="s">
            <div className={styles.label}>
              <Content id={ContentId.DefaultShownQuantity} />
            </div>
            <p className={styles.subtleText}>
              <Content
                id={ContentId.DefaultMaxManualDisplayQuantityDescription}
              />
            </p>
            <PosFormField
              label={<></>}
              errors={formState.errors.defaultManualMaxDisplayQuantity?.message}
              className={styles.textField}
            >
              <PosTextField
                disabled={mutation.isPending}
                type="number"
                {...register('defaultManualMaxDisplayQuantity', {
                  setValueAs: setValueAsNumber,
                  validate: {
                    minValue: (value: number | null) => {
                      return value && value <= 0
                        ? greaterThanZeroRequired
                        : undefined;
                    },
                  },
                })}
              />
            </PosFormField>
          </Stack>
        )}
        {hasAutoSplitDiscontinuousListingSettingFeature && (
          <Stack direction="column" gap="m">
            <Stack direction="row" gap="m" alignItems="center">
              <div className={styles.label}>
                <Content
                  id={ContentId.SplitListingWithDiscontinuousSeatsAutomatically}
                />
              </div>

              <Controller
                control={control}
                name="enableListingSplitOnDiscontinuousOrdinal"
                render={({ field: { value, onChange, ...field } }) => (
                  <Switch
                    disabled={mutation.isPending}
                    checked={Boolean(value)}
                    onChange={(e) => {
                      onChange(e.currentTarget.checked);
                    }}
                    {...field}
                  />
                )}
              />
            </Stack>

            <p className={styles.subtleText}>
              <Content
                id={
                  ContentId.SplitListingWithDiscontinuousSeatsAutomaticallyDescription
                }
              />
            </p>
          </Stack>
        )}

        <div className={styles.footer}>
          <Button
            disabled={mutation.isPending || !formState.isDirty}
            onClick={handleSubmit((data) => {
              mutation.mutate(toSyncCenterSettings(data));
            })}
          >
            <Content id={ContentId.Save} />
          </Button>
        </div>
      </Stack>
    </div>
  );
}
